import { PERMISSION_KEYS, type PermissionKey } from './permissions';

export const ROLES = [
  'admin',
  'proprietar',
  'medic',
  // Un medic care lucrează într-o specialitate cu mai mulți colegi — ortodonția
  // e cazul care a cerut-o. Drepturile sunt ale medicului; ce aduce în plus e
  // grupul: `programari.vede_specializarea` îi arată agenda colegilor de
  // aceeași specialitate, iar rolul o completează singur când e goală.
  'medic_ortodont',
  'asistent',
  'receptie',
  'contabil',
  'hr',
  'readonly',
] as const;

export type Role = (typeof ROLES)[number];

export const LEVELS = ['senior', 'junior'] as const;
export type Level = (typeof LEVELS)[number];

/** Names carried over from the first version of the schema. */
const ROLE_ALIASES: Record<string, Role> = {
  doctor: 'medic',
  reception: 'receptie',
  'read-only': 'readonly',
};

export function normalizeRole(r: string | null | undefined): Role {
  const k = String(r ?? '')
    .toLowerCase()
    .trim();
  if (k in ROLE_ALIASES) return ROLE_ALIASES[k];
  return (ROLES as readonly string[]).includes(k) ? (k as Role) : 'readonly';
}

/**
 * The roles that work at the chair: the doctor and the orthodontist.
 *
 * Every list of doctors — "Medic curant", the patient list's filter, the plan's
 * doctor column, payment history — asked for `role = 'medic'` and so left the
 * orthodontist out, although the orthodontist holds exactly the doctor's
 * rights. In an orthodontic clinic that was half the team. One definition, read
 * by the API and by the screens.
 */
export const CHAIR_ROLES = ['medic', 'medic_ortodont'] as const;

export function isChairRole(role: string | null | undefined): boolean {
  if (role === null || role === undefined || String(role).trim() === '') return false;
  return (CHAIR_ROLES as readonly string[]).includes(normalizeRole(role));
}

export function normalizeLevel(l: string | null | undefined): Level {
  return String(l ?? 'senior').toLowerCase() === 'junior' ? 'junior' : 'senior';
}

/**
 * Default permission set per system role. These seed `rbac_roles` in a new
 * tenant and act as the fallback when a user has no explicit role assignment.
 *
 * The boundaries below are deliberate and were reasoned about in the original;
 * they are preserved here with the reasoning attached.
 */
const BASE_ROLE_PERMS: Record<Exclude<Role, 'medic_ortodont'>, PermissionKey[]> = {
  admin: [...PERMISSION_KEYS],

  // The clinic owner sees the whole business — money, staff, reports — but does
  // not administer the system. No `roluri.gestioneaza`, `useri.gestioneaza` or
  // `setari.modifica`: an owner must not be able to grant themselves rights or
  // break the configuration. No `pacienti.sterge` or `incasari.anuleaza`
  // either: both leave a hole in the books and need a second pair of eyes.
  proprietar: [
    'asistent.foloseste',
    'asistent.programari',
    'asistent.pacienti',
    'asistent.financiar',
    'asistent.stocuri',
    'asistent.rapoarte',
    'asistent.actiuni',
    'sms.pacient',
    'pacienti.vede_toti',
    'pacienti.contacte',
    'pacienti.export',
    'programari.vede',
    'programari.vede_toti_medicii',
    'programari.modifica_finalizate',
    // Câte cereri vin, de unde și câte devin programări: e marketingul lui.
    'cereri.vede',
    'fisa.vede',
    'retete.vede',
    // Evidența sterilizării e ce arată proprietarul la un control.
    'sterilizare.vede',
    'imagistica.vede',
    'imagistica.sedinta',
    'tratament.aproba',
    'financiar.vede',
    'financiar.vede_toti_medicii',
    'incasari.vede',
    'discount.aplica',
    'discount.peste_prag',
    'plan_plata.gestioneaza',
    'cheltuieli.gestioneaza',
    'incasari.inchide_ziua',
    'financiar.blocheaza',
    'stoc.vede',
    'stoc.vede_preturi',
    'furnizori.gestioneaza',
    'rapoarte.vede',
    'rapoarte.financiare',
    // Dashboard-ul propriu. Clinica a cerut pe 14 septembrie dashboard-uri gata
    // făcute pentru administrație, contabilitate și resurse umane, iar până
    // atunci numai administratorul îl putea deschide. Fiecare vede doar
    // indicatorii la care are deja drept.
    'rapoarte.panou',
    'personal.vede',
    'personal.editeaza',
    'concedii.vede',
    'concedii.aproba',
    'pontaj.vede',
    'salarii.vede',
    'recrutare.gestioneaza',
    'jurnal.vede',
    'setari.vede',
    'filiale.gestioneaza',
    'backup.executa',
    'notificari.trimite',
    'chat.foloseste',
    'laborator.vede',
    'laborator.comanda',
    'laborator.config',
    'laborator.costuri',
    // The books, to read and to hand over — not to post or close: the owner
    // does not keep them (contabilitate A1.12).
    'contabilitate.vede',
    'contabilitate.exporta',
  ],

  // HR handles files, leave, attendance and salaries. Deliberately no access to
  // patient clinical records — medical data is the most sensitive category in
  // the system and HR has no need of it.
  hr: [
    'personal.vede',
    'personal.editeaza',
    'personal.documente',
    'concedii.vede',
    'concedii.cere',
    'concedii.aproba',
    'pontaj.vede',
    'pontaj.modifica',
    'salarii.vede',
    // The employee's fiscal data (exemptions, dependants) is HR's to keep.
    'salarii.gestioneaza',
    'salarii.idnp',
    'programari.vede',
    'programari.vede_toti_medicii',
    'rapoarte.vede',
    // Dashboard-ul „Resurse umane” — vezi proprietarul.
    'rapoarte.panou',
    'jurnal.vede',
    'setari.vede',
    'useri.gestioneaza', // creates accounts when someone is hired
    'notificari.trimite',
    'sabloane.gestioneaza',
    'chat.foloseste',
    'recrutare.gestioneaza',
  ],

  // A doctor sees their own agenda only. Not `programari.vede_toti_medicii`
  // (that is a front-desk job) and not `rapoarte.vede` (that is management).
  medic: [
    // Regula clinicii: medicul vede pacienții lui și agenda lui — atât. Doar
    // ortodontul îi vede și pe ai celorlalți ortodonți (`ORTHODONTIST_EXTRA`,
    // mai jos). Hotărârea fondatorului din 18.09: până atunci orice medic cu
    // specialitatea scrisă își vedea colegii de aceeași specialitate — și
    // terapeuții între ei, și chirurgii între ei.
    'pacienti.vede_proprii',
    // Telefonul și adresa pacientului: doar citire, fără
    // `pacienti.contacte_modifica` (hotărârea fondatorului, 18.09). Clinica
    // schimbă treapta din Setări → Roluri, pe rolul de sistem, și alegerea rămâne.
    'pacienti.contacte',
    'programari.vede',
    'programari.creeaza',
    'programari.modifica',
    'programari.muta',
    'fisa.vede',
    'fisa.editeaza',
    'tratament.propune',
    'tratament.aproba',
    'tratament.finalizeaza',
    // Prescrie cine tratează. `retete.emite` nu intră la asistent și nici la
    // recepție, oricâte alte drepturi pe fișă ar avea.
    'retete.vede',
    'retete.emite',
    'sterilizare.vede',
    // Radiografia o citește medicul, și el o semnează. Încărcarea o are și
    // asistenta, fiindcă ea e cea care stă la aparat.
    'imagistica.vede',
    'imagistica.sedinta',
    'imagistica.incarca',
    'imagistica.raporteaza',
    'imagistica.semneaza',
    // Medicul își înregistrează consultația și confirmă ce s-a scris din ea.
    'plan.pret_editeaza',
    // A doctor who may set a price may also lower one. Not above the clinic's
    // threshold, though — `discount.peste_prag` stays with the owner and the
    // administrator, so a large reduction needs a second person. Without this
    // the doctor could set any price at all except a smaller one, which is
    // not a rule anybody would write down.
    'discount.aplica',
    'incasari.vede',
    // Seeing the stock room, not running it. A doctor works out of a cabinet
    // stocked in their own name and asks for what runs out; the original had
    // both — a "Stocul meu" and a "Cererile mele" panel on their dashboard —
    // and gated only the operations. Without this the two panels could never
    // draw anything, the menu entry was hidden, and a doctor who needed
    // composite had to ask on WhatsApp for someone else to type it in.
    // Receiving, consuming, counting and deciding a request all stay behind
    // `stoc.gestioneaza`, which a doctor does not have.
    'stoc.vede',
    'laborator.vede',
    'laborator.comanda',
    'concedii.cere',
    'chat.foloseste',
  ],

  // The assistant works beside one doctor — their agenda, not everyone's.
  asistent: [
    'pacienti.vede_toti',
    // Fără `pacienti.contacte`: asistentul nu vede telefonul și adresa
    // pacienților (hotărârea fondatorului, 18.09). Clinica le poate da înapoi
    // din Setări → Roluri.
    'programari.vede',
    'programari.creeaza',
    'programari.modifica',
    'programari.muta',
    'fisa.vede',
    // The clinic's rule: whoever is at the chair may mark the work done. It
    // does not let the assistant write the plan.
    'tratament.finalizeaza',
    'retete.vede',
    'stoc.vede',
    'stoc.vede_preturi',
    'stoc.gestioneaza',
    // Cine încarcă autoclavul și scrie numărul de pe bandă e tot asistenta.
    'sterilizare.vede',
    'sterilizare.gestioneaza',
    // Filmul îl face asistenta și îl încarcă ea. Ce scrie în el nu e al ei:
    // fără `imagistica.raporteaza` și fără semnătură.
    'imagistica.vede',
    'imagistica.sedinta',
    'imagistica.incarca',
    // Cine numără raftul e asistenta, nu administratorul. Fără cheia asta,
    // inventarierea era a unui singur om din clinică — iar ea are deja dreptul
    // de a recepționa marfă și de a casa, care mișcă stocul la fel de mult.
    'stoc.inventar',
    // Recepția mărfii și comenzile la furnizor — comentariul de mai sus o
    // spunea, dar cheia o avea doar administratorul. Plata facturii rămâne
    // în afară: cere și `furnizori.gestioneaza`, pe care asistenta nu-l are.
    'achizitii.gestioneaza',
    'laborator.vede',
    'laborator.comanda',
    'concedii.cere',
    'chat.foloseste',
  ],

  // Reception needs everyone's agenda — booking across doctors is the job.
  // No analytics.
  receptie: [
    'sms.pacient',
    'pacienti.vede_toti',
    // Recepția sună pacienții și le corectează numărul: citește și scrie.
    'pacienti.contacte',
    'pacienti.contacte_modifica',
    'pacienti.creeaza',
    'pacienti.modifica',
    'programari.vede',
    'programari.creeaza',
    'programari.modifica',
    'programari.anuleaza',
    'programari.muta',
    'programari.vede_toti_medicii',
    // Cererile pacienților noi: recepția răspunde la telefon și la mesaje, deci
    // tot ea le lucrează până la programare.
    'cereri.vede',
    'cereri.gestioneaza',
    // Ședința de dimineață: cine vine azi cu o constatare netratată.
    'imagistica.sedinta',
    'incasari.vede',
    'incasari.inreg',
    // Round 4B: whoever takes the money counts the drawer at the end of the
    // day — the desk(s) of their branch when they are limited to some. The
    // period lock and the month hand-over are not theirs (`financiar.blocheaza`).
    'incasari.inchide_ziua',
    // Also the clinic's rule: the front desk may mark a procedure done — it is
    // the step that puts the debt in front of them — but not change the plan.
    'tratament.finalizeaza',
    // Same reason as the doctor: the front desk is asked "do we have any left"
    // several times a day, and answering it is a read.
    'stoc.vede',
    'stoc.vede_preturi',
    'laborator.vede',
    'laborator.comanda',
    'concedii.cere',
    'chat.foloseste',
  ],

  contabil: [
    'pacienti.vede_toti',
    'pacienti.contacte',
    'financiar.vede',
    'financiar.vede_toti_medicii',
    'incasari.vede',
    // The day's cash check: the accountant's in the original, where the button
    // was drawn for the accountant and the administrator only. Since round 4B
    // reception counts its own drawer too; the accountant keeps the key for
    // the days nobody closed, and alone locks the period.
    'incasari.inchide_ziua',
    'financiar.blocheaza',
    'cheltuieli.gestioneaza',
    'plan_plata.gestioneaza',
    'rapoarte.vede',
    'rapoarte.financiare',
    // Dashboard-ul „Contabilitate” — vezi proprietarul.
    'rapoarte.panou',
    'concedii.cere',
    // The books kept in the application are the accountant's (contabilitate
    // A1.12): reading, posting, closing the month, the chart of accounts and
    // the monthly package, and the counterparties whose fiscal data they post.
    'contabilitate.vede',
    'contabilitate.posteaza',
    'contabilitate.nota',
    'contabilitate.inchide',
    // The year's adjustments after December closed (§8): the accountant's alone.
    'contabilitate.ajustari',
    'contabilitate.plan_conturi',
    'contabilitate.exporta',
    'contrapartide.gestioneaza',
    // The payroll is the accountant's: compute, approve, pay, IPC21 (B2) —
    // which names each employee by IDNP.
    'salarii.vede',
    'salarii.gestioneaza',
    'salarii.idnp',
  ],

  readonly: [
    'pacienti.vede_toti',
    'pacienti.contacte',
    'programari.vede',
    'fisa.vede',
    'imagistica.vede',
  ],
};

/**
 * Rolurile de sistem, cu drepturile lor.
 *
 * Ortodontul are **exact** drepturile medicului — aceeași listă, nu o copie
 * care poate să rămână în urmă. Ce aduce rolul e grupul: agenda o vede a
 * colegilor de aceeași specialitate (`programari.vede_specializarea`, pe care
 * medicul o are deja), iar specialitatea o completează serviciul de personal
 * când e goală, ca grupul să existe fără să depindă de ce a scris cineva cu
 * mâna în câmpul liber.
 */
/**
 * Ce are ortodontul în plus față de medic: agenda și fișele colegilor de
 * aceeași specialitate. Ortodonții lucrează pe cazuri lungi, cu activări
 * lunare, și își acoperă unul altuia pacienții; un terapeut sau un chirurg nu
 * are nevoie de ziua colegului (hotărârea fondatorului, 18.09).
 */
const ORTHODONTIST_EXTRA: PermissionKey[] = [
  'programari.vede_specializarea',
  'pacienti.vede_specializarea',
];

export const SYSTEM_ROLE_PERMS: Record<Role, PermissionKey[]> = (() => {
  const perms: Record<Role, PermissionKey[]> = {
    ...BASE_ROLE_PERMS,
    medic_ortodont: [...BASE_ROLE_PERMS.medic, ...ORTHODONTIST_EXTRA],
  };
  // The assistant's front door is open to every clinic worker — every system
  // role here is staff (a patient is not a role). The per-subject keys
  // (`asistent.programari`, `asistent.pacienti`, …) stay role-scoped, so each
  // person's assistant only reaches the data they could already open on their
  // own screens; opening the door adds the in-app help and nothing they cannot
  // already see.
  for (const role of Object.keys(perms) as Role[]) {
    if (!perms[role].includes('asistent.foloseste')) {
      perms[role] = [...perms[role], 'asistent.foloseste'];
    }
  }
  return perms;
})();

export interface SystemRoleDefinition {
  key: Role;
  name: string;
  description: string;
}

export const SYSTEM_ROLES: SystemRoleDefinition[] = [
  {
    key: 'admin',
    name: 'Administrator',
    description: 'Acces complet la sistem, inclusiv roluri și configurare.',
  },
  {
    key: 'proprietar',
    name: 'Proprietar',
    description:
      'Vede tot ce ține de afacere: bani, personal, rapoarte. Nu administrează sistemul.',
  },
  { key: 'medic', name: 'Medic', description: 'Acces clinic: pacienți, programări, tratamente.' },
  {
    key: 'medic_ortodont',
    name: 'Medic ortodont',
    description:
      'Ca medicul, plus agenda colegilor de aceeași specialitate. Specialitatea se completează singură cu „Ortodonție" dacă e goală.',
  },
  { key: 'asistent', name: 'Asistent', description: 'Sprijin clinic și gestiune stoc.' },
  {
    key: 'receptie',
    name: 'Recepție',
    description: 'Programări, pacienți și încasări la recepție.',
  },
  { key: 'contabil', name: 'Contabil', description: 'Acces financiar și rapoarte.' },
  {
    key: 'hr',
    name: 'Resurse umane',
    description: 'Dosare de personal, concedii, pontaj și salarii. Fără acces la fișele medicale.',
  },
  { key: 'readonly', name: 'Doar citire', description: 'Vizualizare, fără modificări.' },
];

/**
 * Seniority narrows what a role may do. A junior doctor proposes treatment but
 * does not approve it; a junior receptionist books but does not cancel.
 */
export const LEVEL_ADJUST: Partial<
  Record<Role, Partial<Record<Level, { grant?: PermissionKey[]; deny?: PermissionKey[] }>>>
> = {
  medic: { junior: { deny: ['tratament.aproba', 'imagistica.semneaza'] } },
  medic_ortodont: { junior: { deny: ['tratament.aproba', 'imagistica.semneaza'] } },
  receptie: { junior: { deny: ['programari.anuleaza'] } },
};
